import { getAgencyServicePromiseClient } from '../utils/grpcClient';
import {
    GetAgencyProfileRequest,
    UpdateAgencyProfileRequest,
} from '../generated/fitness-service/v1/agency_pb';

export interface AgencyProfile {
    id: string;
    name: string;
    description: string;
    address: string;
    contactPhone: string;
    contactEmail: string;
    isActive: boolean;
    createdAt?: Date;
    trainerCount: number;
    // "Your own branded app" tier.
    brandingEnabled: boolean;
    brandName: string;
    brandLogoUrl: string;
    brandPrimaryColor: string;
    brandSupportEmail: string;
    isIndependent: boolean;
}

export interface AgencyBrandInput {
    brandName?: string;
    brandLogoUrl?: string;
    brandPrimaryColor?: string;
    brandSupportEmail?: string;
}

function mapAgencyProfile(agency: any): AgencyProfile {
    return {
        id: agency.getId(),
        name: agency.getName(),
        description: agency.getDescription(),
        address: agency.getAddress(),
        contactPhone: agency.getContactPhone(),
        contactEmail: agency.getContactEmail(),
        isActive: agency.getIsActive(),
        createdAt: agency.getCreatedAt()?.toDate(),
        trainerCount: agency.getTrainerCount(),
        brandingEnabled: agency.getBrandingEnabled(),
        brandName: agency.getBrandName(),
        brandLogoUrl: agency.getBrandLogoUrl(),
        brandPrimaryColor: agency.getBrandPrimaryColor(),
        brandSupportEmail: agency.getBrandSupportEmail(),
        isIndependent: agency.getIsIndependent(),
    };
}

export class AgencyService {
    private client = getAgencyServicePromiseClient();

    private getAuthMetadata() {
        const token = localStorage.getItem('authToken');
        if (!token) {
            throw new Error('No access token found');
        }
        return { authorization: `Bearer ${token}` };
    }

    async getAgencyProfile(): Promise<AgencyProfile> {
        const request = new GetAgencyProfileRequest();
        
        const response = await this.client.getAgencyProfile(request, this.getAuthMetadata());
        const agency = response.getAgency();
        
        if (!agency) {
            throw new Error('No agency profile found');
        }

        return mapAgencyProfile(agency);
    }

    /// Update this agency's brand content. Empty strings are left unchanged by
    /// the server, and everything is ignored unless branding is enabled.
    async updateBranding(input: AgencyBrandInput): Promise<AgencyProfile> {
        const request = new UpdateAgencyProfileRequest();
        request.setBrandName(input.brandName ?? '');
        request.setBrandLogoUrl(input.brandLogoUrl ?? '');
        request.setBrandPrimaryColor(input.brandPrimaryColor ?? '');
        request.setBrandSupportEmail(input.brandSupportEmail ?? '');

        const response = await this.client.updateAgencyProfile(request, this.getAuthMetadata());
        const agency = response.getAgency();
        if (!agency) {
            throw new Error('No agency profile returned');
        }
        return mapAgencyProfile(agency);
    }

    // Generate a signup link with pre-filled agency code
    generateTrainerSignupLink(agencyName: string): string {
        const baseUrl = window.location.origin;
        const signupUrl = new URL('/auth/trainer-signup', baseUrl);
        
        // Add agency code as query parameter (using agency name as code for now)
        signupUrl.searchParams.set('agency_code', agencyName);
        signupUrl.searchParams.set('role', 'trainer');
        
        return signupUrl.toString();
    }
}

export const agencyService = new AgencyService();